<template>
	<div class="admin-hub-page">
		<div class="admin-hub-header">
			<router-link :to="homeRoute" class="back-link">← {{ $t("adminPage.backToHome") }}</router-link>
			<h1>{{ $t("adminPage.title") }}</h1>
			<p>{{ $t("adminPage.subtitle") }}</p>
		</div>

		<div class="tiles-grid">
			<button v-if="canManageProjects" type="button" class="tile" @click="openProjectModal">
				<AppIcon :name="IconName.PencilBox" class="icon" :size="28" />
				<div class="content">
					<span class="title">{{
						currentProject?.type !== ProjectType.Workspace ? $t("project.updateProject") : $t("project.createProject")
					}}</span>
					<span class="description">{{ $t("adminPage.projectDescription") }}</span>
				</div>
			</button>

			<router-link
				v-if="isUserLoggedIn && store.currentUser?.has(Permission.AuthorizationWrite)"
				to="/permission"
				class="tile"
			>
				<AppIcon :name="IconName.ShieldPin" class="icon" :size="28" />
				<div class="content">
					<span class="title">{{ $t("permissionPage.title") }}</span>
					<span class="description">{{ $t("permissionPage.description") }}</span>
				</div>
			</router-link>

			<router-link
				v-if="isUserLoggedIn && store.currentUser?.has(Permission.AuthorizationRead)"
				to="/permission-matrix"
				class="tile"
			>
				<AppIcon :name="IconName.Table" class="icon" :size="28" />
				<div class="content">
					<span class="title">{{ $t("permissionMatrixPage.title") }}</span>
					<span class="description">{{ $t("adminPage.permissionMatrixDescription") }}</span>
				</div>
			</router-link>

			<router-link
				v-if="isUserLoggedIn && store.currentUser?.has(Permission.AuthorizationWrite)"
				to="/admin-panel"
				class="tile"
			>
				<AppIcon :name="IconName.Tag" class="icon" :size="28" />
				<div class="content">
					<span class="title">{{ $t("adminPanelPage.title") }}</span>
					<span class="description">{{ $t("adminPanelPage.description") }}</span>
				</div>
			</router-link>

			<a
				v-if="isUserLoggedIn && store.currentUser?.has(Permission.DashboardRead) && dashboardUrl"
				:href="dashboardUrl"
				class="tile"
			>
				<AppIcon :name="IconName.IdCard" class="icon" :size="28" />
				<div class="content">
					<span class="title">Dashboard</span>
					<span class="description">{{ $t("adminPage.dashboardDescription") }}</span>
				</div>
			</a>
		</div>

		<ProjectModal
			:is-open="isProjectModalOpen"
			:current-project="currentProject?.type !== ProjectType.Workspace ? currentProject : null"
			@close="isProjectModalOpen = false"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { Permission, UseRootStore } from "@/model/RootStore"
import { ProjectStoreKey } from "@/stores/projectStore"
import { ProjectType } from "@/graphql"
import { config, Feature } from "@/AppConfig"
import ProjectModal from "@/components/modals/ProjectModal.vue"
import { firstQueryValue } from "@/utils/routing"

const router = useRouter()
const route = useRoute()
const store = UseRootStore()
const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)

const isProjectModalOpen = ref(false)

const isUserLoggedIn = computed(() => store.isUserLoggedIn)

const canManageProjects = computed(() => store.currentUser?.has(Permission.ProjectWrite))

const hasAdminAccess = computed(() => {
	return (
		canManageProjects.value ||
		(isUserLoggedIn.value &&
			(store.currentUser?.has(Permission.AuthorizationRead) ||
				store.currentUser?.has(Permission.AuthorizationWrite) ||
				store.currentUser?.has(Permission.DashboardRead)))
	)
})

const dashboardUrl = computed(() => {
	const configured = import.meta.env.VITE_DASHBOARD_URL as string | undefined
	if (configured) return configured.replace(/\/$/, "")
	if (import.meta.env.DEV) return "http://localhost:3000"
	return undefined
})

const scopedProjectId = computed(() => firstQueryValue(route.query.project) ?? currentProject.value?.id)
const homeRoute = computed(() => ({
	path: "/",
	query: scopedProjectId.value ? { project: scopedProjectId.value } : {},
}))

async function openProjectModal(): Promise<void> {
	if (scopedProjectId.value) {
		try {
			await projectStore.fetchProjectMetadata(scopedProjectId.value, { includePeople: true })
		} catch (error) {
			console.error("Failed to fetch editable project details:", error)
		}
	}

	isProjectModalOpen.value = true
}

onMounted(() => {
	if (!hasAdminAccess.value) {
		router.push("/")
	}
})
</script>

<style scoped>
.admin-hub-page {
	max-width: 1024px;
	margin: 0 auto;
	padding: var(--spacing-2xl) var(--spacing-xl);
}

.admin-hub-header {
	margin-bottom: var(--spacing-xl);

	.back-link {
		display: inline-block;
		color: var(--neutral-600);
		text-decoration: none;
		margin-bottom: var(--spacing-md);
		font-size: var(--font-size-base);

		&:hover {
			color: var(--neutral-900);
		}
	}

	h1 {
		margin: 0 0 var(--spacing-xs);
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--neutral-900);
	}

	p {
		margin: 0;
		color: var(--neutral-600);
		font-size: var(--font-size-base);
	}
}

.tiles-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: var(--spacing-md);
}

.tile {
	display: flex;
	align-items: flex-start;
	gap: var(--spacing-md);
	padding: var(--spacing-lg);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	background: var(--neutral-0);
	text-align: left;
	text-decoration: none;
	color: inherit;
	cursor: pointer;
	transition:
		border-color 0.15s ease,
		box-shadow 0.15s ease;

	.icon {
		flex-shrink: 0;
		color: var(--primary-green);
	}

	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}

	.title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-900);
	}

	.description {
		font-size: var(--font-size-small);
		color: var(--neutral-600);
	}

	&:hover {
		border-color: var(--primary-green);
		box-shadow: var(--shadow-sm);
	}
}
</style>
